import { Directive, ElementRef, Input, OnInit, Optional, Host, Renderer2, AfterViewInit } from '@angular/core';
import { ControlContainer, AbstractControl, Validators, FormGroupDirective, NgForm } from '@angular/forms';

@Directive({
  selector: '[appRequiredField]'
})
export class RequiredFieldDirective implements OnInit, AfterViewInit {
  @Input('appRequiredField') formControlName: string;
  private control: AbstractControl | null = null;

  constructor( 
    private el: ElementRef,
    private renderer: Renderer2,
    @Optional() @Host() private controlContainer: ControlContainer,
    @Optional() private formGroupDirective: FormGroupDirective,
    @Optional() private ngForm: NgForm
  ) {}

  ngOnInit() {
    this.findControl();
  }

  ngAfterViewInit() {
    if (!this.control) {
      setTimeout(() => this.findControl(), 0);
    }
  }

  private findControl() {
    if (!this.controlContainer) return;

    const formGroup = this.controlContainer.control;
    
    // Handle nested form controls (e.g., "primaryContact.name")
    if (this.formControlName.includes('.')) {
      // Try root form first
      this.control = this.formGroupDirective?.form?.get(this.formControlName) 
        || this.ngForm?.form?.get(this.formControlName) 
        || formGroup?.get(this.formControlName) 
        || null;
      
      // Try traversing up if still not found
      if (!this.control && formGroup) {
        let currentGroup: any = formGroup;
        for (let depth = 0; depth < 5 && currentGroup && !this.control; depth++) {
          this.control = currentGroup.get(this.formControlName) || null;
          currentGroup = currentGroup.parent;
        }
      }
    } else {
      this.control = formGroup?.get(this.formControlName) || null;
    }

    if (this.control && this.isControlRequired(this.control)) {
      this.addAsterisk();
    }
  }

  private addAsterisk() {
    const labelElement = this.el.nativeElement as HTMLLabelElement;
    if (labelElement.querySelector('span[data-required-asterisk]')) return;

    const asterisk = this.renderer.createElement('span');
    this.renderer.setAttribute(asterisk, 'data-required-asterisk', 'true');
    this.renderer.setStyle(asterisk, 'color', 'red');
    this.renderer.setStyle(asterisk, 'font-weight', 'bold');
    this.renderer.setStyle(asterisk, 'margin-left', '2px');
    this.renderer.setProperty(asterisk, 'innerHTML', '*');
    this.renderer.appendChild(labelElement, asterisk);
  }

  private isControlRequired(control: AbstractControl): boolean {
    if (!control) return false;

    // Check if validator is Validators.required directly
    if (control.validator === Validators.required) return true;

    // Check validator result with empty value
    if (control.validator) {
      const result = control.validator({ value: null } as AbstractControl);
      if (result && result['required']) return true;
    }

    // Check validators array
    const validators = (control as any)._rawValidators || (control as any).validators;
    if (Array.isArray(validators)) {
      return validators.some((v: any) => {
        if (v === Validators.required) return true;
        if (typeof v === 'function') {
          const result = v({ value: null } as AbstractControl);
          return result && result['required'] !== undefined;
        }
        return false;
      });
    }

    return false;
  }

  // COMMENTED OUT - Error messages are handled in HTML template
  // private displayErrors(control: AbstractControl) {
  //   const parentElement = this.el.nativeElement.parentElement;
  //   if (!parentElement) return;

  //   // Check if there are already manual error messages in the template
  //   const manualErrors = parentElement.querySelectorAll('.text-danger:not([data-directive-error])');
  //   const hasManualErrors = manualErrors.length > 0 && 
  //     Array.from(manualErrors).some((el: any) => 
  //       el.textContent?.trim() && 
  //       !el.hasAttribute('data-directive-error')
  //     );

  //   // Only add directive errors if there are no manual error messages
  //   if (!hasManualErrors) {
  //     const errorMessages = this.getErrorMessages(control);
      
  //     // Remove only directive-generated error messages
  //     const existingDirectiveErrors = parentElement.querySelectorAll('[data-directive-error]');
  //     existingDirectiveErrors.forEach(error => parentElement.removeChild(error));

  //     // Add new error messages with marker
  //     errorMessages.forEach(msg => {
  //       const errorElement = this.renderer.createElement('div');
  //       this.renderer.addClass(errorElement, 'text-danger');
  //       this.renderer.setAttribute(errorElement, 'data-directive-error', 'true');
  //       this.renderer.setStyle(errorElement, 'display', 'block');
  //       this.renderer.setProperty(errorElement, 'innerHTML', msg);
  //       this.renderer.appendChild(parentElement, errorElement);
  //     });
  //   } else {
  //     // Remove directive errors if manual errors exist
  //     const existingDirectiveErrors = parentElement.querySelectorAll('[data-directive-error]');
  //     existingDirectiveErrors.forEach(error => parentElement.removeChild(error));
  //   }
  // }

  // private getErrorMessages(control: AbstractControl): string[] {
  //   if (!control.errors) {
  //     return [];
  //   }

  //   const errorMessages: string[] = [];
  //   const errorMap = {
  //     required: 'This field is required.',
  //     email: 'Enter a valid email.',
  //     minlength: `Minimum length is ${control.errors['minlength']?.requiredLength} characters.`,
  //     maxlength: `Maximum length is ${control.errors['maxlength']?.requiredLength} characters.`,
  //     pastDate: 'Dates in the past are not allowed.' // Custom error message
  //     //pattern: 'The value entered does not match the required pattern.',
  //   };

  //   Object.keys(control.errors).forEach(key => {
  //     if (errorMap[key]) {
  //       errorMessages.push(errorMap[key]);
  //     }
  //   });

  //   return errorMessages;
  // }
}
